import type { Topic } from "../types";
import { todayInShanghai } from "./time";

const WEEKDAYS = ["日", "一", "二", "三", "四", "五", "六"] as const;
const DAY_MS = 24 * 60 * 60 * 1000;

export type ReviewUrgency = "overdue" | "soon" | "later" | "none";

export interface ReviewWhen {
  kind: "unscheduled" | "today" | "tomorrow" | "dated";
  time: string | null;
  weekday: string;
  monthDay: string;
  urgency: ReviewUrgency;
  sortKey: number;
}

export function reviewWhen(
  topic: Pick<Topic, "plannedPublishAt" | "plannedPublishDate">,
  now = new Date(),
): ReviewWhen {
  const planned = plannedMoment(topic);
  if (!planned) {
    return { kind: "unscheduled", time: null, weekday: "", monthDay: "", urgency: "none", sortKey: Number.POSITIVE_INFINITY };
  }
  const today = todayInShanghai(now);
  const tomorrow = addDays(today, 1);
  const kind = planned.iso === today ? "today" : planned.iso === tomorrow ? "tomorrow" : "dated";
  return {
    kind,
    time: planned.time,
    weekday: weekdayOf(planned.iso),
    monthDay: `${planned.iso.slice(5, 7)}/${planned.iso.slice(8, 10)}`,
    urgency: urgencyOf(planned, today, now.getTime()),
    sortKey: planned.sortKey,
  };
}

export function sortReviewQueue<T extends Pick<Topic, "plannedPublishAt" | "plannedPublishDate" | "title">>(
  topics: T[],
  now = new Date(),
): T[] {
  return [...topics].sort((left, right) => compareReviewWhen(left, right, now));
}

export function countSoonReviews(topics: Pick<Topic, "plannedPublishAt" | "plannedPublishDate">[], now = new Date()): number {
  return topics.filter((topic) => reviewWhen(topic, now).urgency === "soon").length;
}

function compareReviewWhen(
  left: Pick<Topic, "plannedPublishAt" | "plannedPublishDate" | "title">,
  right: Pick<Topic, "plannedPublishAt" | "plannedPublishDate" | "title">,
  now: Date,
): number {
  const leftKey = reviewWhen(left, now).sortKey;
  const rightKey = reviewWhen(right, now).sortKey;
  if (leftKey !== rightKey) {
    if (!Number.isFinite(leftKey)) return 1;
    if (!Number.isFinite(rightKey)) return -1;
    return leftKey - rightKey;
  }
  return left.title.localeCompare(right.title, "zh");
}

function plannedMoment(
  topic: Pick<Topic, "plannedPublishAt" | "plannedPublishDate">,
): { iso: string; time: string | null; sortKey: number } | null {
  const raw = topic.plannedPublishAt?.trim() ?? "";
  if (raw.includes("T")) {
    const ms = Date.parse(raw);
    if (!Number.isNaN(ms)) {
      const clock = shanghaiClock(new Date(ms));
      return { iso: clock.iso, time: clock.time, sortKey: ms };
    }
  }
  const date = /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : topic.plannedPublishDate;
  if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  const end = Date.parse(`${date}T23:59:59.999+08:00`);
  if (Number.isNaN(end)) return null;
  return { iso: date, time: null, sortKey: end };
}

function urgencyOf(
  planned: { iso: string; time: string | null; sortKey: number },
  today: string,
  nowMs: number,
): ReviewUrgency {
  if (planned.time) {
    if (planned.sortKey < nowMs) return "overdue";
    return planned.sortKey - nowMs <= DAY_MS ? "soon" : "later";
  }
  if (planned.iso < today) return "overdue";
  const start = Date.parse(`${planned.iso}T00:00:00+08:00`);
  if (!Number.isFinite(start)) return "later";
  return start - nowMs <= DAY_MS ? "soon" : "later";
}

function shanghaiClock(value: Date): { iso: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Shanghai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const hour = read("hour") === "24" ? "00" : read("hour");
  return { iso: `${read("year")}-${read("month")}-${read("day")}`, time: `${hour}:${read("minute")}` };
}

function addDays(iso: string, days: number): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return iso;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + days));
  return date.toISOString().slice(0, 10);
}

function weekdayOf(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return "";
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return WEEKDAYS[date.getUTCDay()] ?? "";
}
